/**
 * Obsidian-style live preview for CodeMirror 6.
 *
 * Raw markdown stays the document truth; this plugin renders constructs
 * (headings, emphasis, links, checkboxes, ...) as formatted text and hides
 * their syntax marks — EXCEPT for the construct the selection currently
 * touches, whose raw syntax is revealed for editing. Every decoration is
 * view-only; the document is never rewritten.
 */
import { Facet, RangeSet, type Range } from "@codemirror/state";
import {
  Decoration,
  type DecorationSet,
  EditorView,
  ViewPlugin,
  type ViewUpdate,
  WidgetType,
} from "@codemirror/view";
import { syntaxTree } from "@codemirror/language";
import type { SyntaxNode } from "@lezer/common";
import { tableField, tableStyles } from "./table-widget";
import { mermaidField, mermaidStyles } from "./mermaid-widget";

// ---------------------------------------------------------------------------
// App callbacks (navigation etc.) provided from outside the editor.
// ---------------------------------------------------------------------------

export interface EditorCallbacks {
  onWikilinkClick: (target: string) => void;
  onLinkClick: (url: string) => void;
  onHashtagClick: (tag: string) => void;
}

const defaultCallbacks: EditorCallbacks = {
  onWikilinkClick: () => {},
  onLinkClick: (url) => window.open(url, "_blank", "noopener,noreferrer"),
  onHashtagClick: () => {},
};

export const editorCallbacks = Facet.define<Partial<EditorCallbacks>, EditorCallbacks>({
  combine: (values) => Object.assign({}, defaultCallbacks, ...values),
});

// ---------------------------------------------------------------------------
// Widgets
// ---------------------------------------------------------------------------

class CheckboxWidget extends WidgetType {
  constructor(
    readonly checked: boolean,
    readonly markerFrom: number,
    readonly markerTo: number,
  ) {
    super();
  }

  override eq(other: CheckboxWidget) {
    return other.checked === this.checked && other.markerFrom === this.markerFrom;
  }

  toDOM(view: EditorView) {
    const wrap = document.createElement("label");
    wrap.className = "hn-checkbox";
    wrap.setAttribute("aria-label", this.checked ? "Mark task incomplete" : "Mark task complete");
    const box = document.createElement("input");
    box.type = "checkbox";
    box.checked = this.checked;
    box.tabIndex = -1;
    wrap.appendChild(box);
    wrap.addEventListener("mousedown", (e) => {
      e.preventDefault();
      view.dispatch({
        changes: { from: this.markerFrom, to: this.markerTo, insert: this.checked ? "[ ]" : "[x]" },
      });
    });
    return wrap;
  }

  override ignoreEvent() {
    return true;
  }
}

class BulletWidget extends WidgetType {
  override eq() {
    return true;
  }
  toDOM() {
    const span = document.createElement("span");
    span.className = "hn-list-bullet";
    span.textContent = "•";
    return span;
  }
}

class HrWidget extends WidgetType {
  override eq() {
    return true;
  }
  toDOM() {
    const span = document.createElement("span");
    span.className = "hn-hr-widget";
    span.appendChild(document.createElement("hr"));
    return span;
  }
}

class ImageWidget extends WidgetType {
  constructor(
    readonly src: string,
    readonly alt: string,
  ) {
    super();
  }
  override eq(other: ImageWidget) {
    return other.src === this.src && other.alt === this.alt;
  }
  toDOM() {
    const wrap = document.createElement("span");
    wrap.className = "hn-image-widget";
    const img = document.createElement("img");
    img.src = this.src;
    img.alt = this.alt;
    img.loading = "lazy";
    img.onerror = () => {
      wrap.textContent = `⚠ image failed to load: ${this.alt || this.src}`;
      wrap.className = "hn-image-broken";
    };
    wrap.appendChild(img);
    return wrap;
  }
  override ignoreEvent() {
    return false;
  }
}

// ---------------------------------------------------------------------------
// The plugin
// ---------------------------------------------------------------------------

const HEADING_RE = /^ATXHeading(\d)$/;

function selectionTouches(view: EditorView, from: number, to: number): boolean {
  for (const r of view.state.selection.ranges) {
    if (r.from <= to && r.to >= from) return true;
  }
  return false;
}

/** Block constructs count the whole line(s) as their active zone. */
function selectionTouchesLines(view: EditorView, from: number, to: number): boolean {
  const start = view.state.doc.lineAt(from).from;
  const end = view.state.doc.lineAt(to).to;
  return selectionTouches(view, start, end);
}

function hasFocusAnd(view: EditorView, active: boolean): boolean {
  // When the editor is unfocused, everything renders (nothing is "active").
  return view.hasFocus && active;
}

class LivePreviewPlugin {
  decorations: DecorationSet;
  atomics: RangeSet<Decoration>;

  constructor(view: EditorView) {
    ({ decorations: this.decorations, atomics: this.atomics } = this.build(view));
  }

  update(u: ViewUpdate) {
    if (u.docChanged || u.selectionSet || u.viewportChanged || u.focusChanged || syntaxTree(u.state) !== syntaxTree(u.startState)) {
      ({ decorations: this.decorations, atomics: this.atomics } = this.build(u.view));
    }
  }

  build(view: EditorView): { decorations: DecorationSet; atomics: RangeSet<Decoration> } {
    const deco: Range<Decoration>[] = [];
    const atomic: Range<Decoration>[] = [];
    const doc = view.state.doc;
    const tree = syntaxTree(view.state);

    const hide = (from: number, to: number) => {
      if (to > from) deco.push(Decoration.replace({}).range(from, to));
    };

    /** Hide a syntax mark plus one trailing space if present. */
    const hideWithSpace = (from: number, to: number) => {
      const next = doc.sliceString(to, to + 1);
      hide(from, next === " " ? to + 1 : to);
    };

    for (const { from, to } of view.visibleRanges) {
      tree.iterate({
        from,
        to,
        enter: (nodeRef) => {
          const name = nodeRef.name;
          const node = nodeRef.node;
          const nFrom = nodeRef.from;
          const nTo = nodeRef.to;

          // ---------- Headings ----------
          const h = HEADING_RE.exec(name);
          if (h) {
            const line = doc.lineAt(nFrom);
            deco.push(Decoration.line({ class: `hn-h${h[1]}` }).range(line.from));
            if (!hasFocusAnd(view, selectionTouchesLines(view, nFrom, nTo))) {
              for (const mark of node.getChildren("HeaderMark")) {
                hideWithSpace(mark.from, mark.to);
              }
            }
            return;
          }

          switch (name) {
            // ---------- Subtext (-# small dimmed line) ----------
            case "Subtext": {
              deco.push(Decoration.line({ class: "hn-subtext" }).range(doc.lineAt(nFrom).from));
              if (!hasFocusAnd(view, selectionTouchesLines(view, nFrom, nTo))) {
                const mark = node.getChild("SubtextMark");
                if (mark) hideWithSpace(mark.from, mark.to);
              }
              return;
            }

            // ---------- Inline emphasis-family ----------
            case "Emphasis":
            case "StrongEmphasis":
            case "Strikethrough":
            case "MarkHighlight": {
              if (!hasFocusAnd(view, selectionTouches(view, nFrom, nTo))) {
                const markName =
                  name === "Strikethrough" ? "StrikethroughMark" : name === "MarkHighlight" ? "MarkHighlightMark" : "EmphasisMark";
                for (const mark of node.getChildren(markName)) hide(mark.from, mark.to);
              }
              return;
            }

            case "InlineCode": {
              const marks = node.getChildren("CodeMark");
              const first = marks[0];
              const last = marks[marks.length - 1];
              if (first && last && last.from > first.to) {
                deco.push(Decoration.mark({ class: "hn-inline-code" }).range(first.to, last.from));
              }
              if (!hasFocusAnd(view, selectionTouches(view, nFrom, nTo))) {
                for (const mark of marks) hide(mark.from, mark.to);
              }
              return;
            }

            // ---------- Links ----------
            case "Link": {
              const marks = node.getChildren("LinkMark");
              const url = node.getChild("URL");
              // `[text]` without a URL is a shortcut reference (or a callout
              // marker) — leave it raw instead of eating the brackets
              if (!url) return;
              const textFrom = marks[0]?.to ?? nFrom;
              const textTo = marks[1]?.from ?? nTo;
              if (textTo > textFrom && url) {
                deco.push(
                  Decoration.mark({
                    class: "hn-link",
                    attributes: { "data-url": doc.sliceString(url.from, url.to), title: doc.sliceString(url.from, url.to) },
                  }).range(textFrom, textTo),
                );
              }
              if (!hasFocusAnd(view, selectionTouches(view, nFrom, nTo))) {
                // hide [ then ](url...) — everything except the label
                const open = marks[0];
                const closeBracket = marks[1];
                if (open) hide(open.from, open.to);
                if (closeBracket) hide(closeBracket.from, nTo);
              }
              return;
            }

            case "Image": {
              const url = node.getChild("URL");
              if (!url) return;
              const src = doc.sliceString(url.from, url.to);
              const altMarks = node.getChildren("LinkMark");
              const alt =
                altMarks.length >= 2 ? doc.sliceString(altMarks[0]!.to, altMarks[1]!.from) : "";
              if (!hasFocusAnd(view, selectionTouchesLines(view, nFrom, nTo)) && /^(https?:|data:|blob:|\/)/.test(src)) {
                const w = Decoration.replace({ widget: new ImageWidget(src, alt) });
                deco.push(w.range(nFrom, nTo));
                atomic.push(w.range(nFrom, nTo));
              }
              return;
            }

            // ---------- Wikilinks ----------
            case "Wikilink": {
              const inner = doc.sliceString(nFrom + 2, nTo - 2);
              const pipe = inner.indexOf("|");
              const target = pipe === -1 ? inner : inner.slice(0, pipe);
              deco.push(
                Decoration.mark({
                  class: "hn-wikilink",
                  attributes: { "data-wikilink": target.trim() },
                }).range(nFrom, nTo),
              );
              if (!hasFocusAnd(view, selectionTouches(view, nFrom, nTo))) {
                hide(nFrom, nFrom + 2);
                hide(nTo - 2, nTo);
                if (pipe !== -1) {
                  // [[target|alias]] -> show alias only
                  hide(nFrom + 2, nFrom + 2 + pipe + 1);
                }
              }
              return;
            }

            case "Hashtag": {
              deco.push(Decoration.mark({ class: "hn-tag-pill", attributes: { "data-tag": doc.sliceString(nFrom + 1, nTo) } }).range(nFrom, nTo));
              return;
            }

            // ---------- Block quotes ----------
            case "Blockquote": {
              const startLine = doc.lineAt(nFrom).number;
              const endLine = doc.lineAt(nTo).number;
              for (let ln = startLine; ln <= endLine; ln++) {
                const line = doc.line(ln);
                deco.push(Decoration.line({ class: "hn-blockquote" }).range(line.from));
              }
              return;
            }

            case "QuoteMark": {
              const line = doc.lineAt(nFrom);
              if (!hasFocusAnd(view, selectionTouches(view, line.from, line.to))) {
                hideWithSpace(nFrom, nTo);
              }
              return;
            }

            // ---------- Fenced code ----------
            case "FencedCode": {
              const startLine = doc.lineAt(nFrom).number;
              const endLine = doc.lineAt(nTo).number;
              for (let ln = startLine; ln <= endLine; ln++) {
                const line = doc.line(ln);
                const cls = ["hn-codeblock"];
                if (ln === startLine) cls.push("hn-codeblock-first", "hn-fence");
                if (ln === endLine) cls.push("hn-codeblock-last", "hn-fence");
                deco.push(Decoration.line({ class: cls.join(" ") }).range(line.from));
              }
              return;
            }

            // ---------- Lists ----------
            case "ListMark": {
              const line = doc.lineAt(nFrom);
              const active = hasFocusAnd(view, selectionTouches(view, line.from, line.to));
              const markText = doc.sliceString(nFrom, nTo);
              const parent = node.parent; // ListItem
              const task = parent?.getChild("Task");
              if (task ?? parent?.firstChild?.name === "Task") {
                // Task item: hide "- " entirely (checkbox widget replaces the marker)
                if (!active) hideWithSpace(nFrom, nTo);
                return;
              }
              if (/^[-*+]$/.test(markText) && !active) {
                const w = Decoration.replace({ widget: new BulletWidget() });
                deco.push(w.range(nFrom, nTo));
              }
              return;
            }

            case "TaskMarker": {
              const line = doc.lineAt(nFrom);
              const active = hasFocusAnd(view, selectionTouches(view, line.from, line.to));
              const checked = /x/i.test(doc.sliceString(nFrom, nTo));
              if (!active) {
                const w = Decoration.replace({ widget: new CheckboxWidget(checked, nFrom, nTo) });
                hideWithSpace(nFrom, nFrom); // no-op keeps ordering stable
                deco.push(w.range(nFrom, Math.min(nTo + 1, line.to)));
                atomic.push(w.range(nFrom, Math.min(nTo + 1, line.to)));
              }
              if (checked) {
                deco.push(Decoration.mark({ class: "hn-task-done" }).range(Math.min(nTo + 1, line.to), line.to));
              }
              return;
            }

            // ---------- Escapes: render "\." as "." (hide the backslash) ----------
            case "Escape": {
              const line = doc.lineAt(nFrom);
              if (!hasFocusAnd(view, selectionTouches(view, line.from, line.to))) {
                hide(nFrom, nFrom + 1);
              }
              return;
            }

            // ---------- Horizontal rule ----------
            case "HorizontalRule": {
              if (!hasFocusAnd(view, selectionTouchesLines(view, nFrom, nTo))) {
                const w = Decoration.replace({ widget: new HrWidget() });
                deco.push(w.range(nFrom, nTo));
                atomic.push(w.range(nFrom, nTo));
              }
              return;
            }

            default:
              return;
          }
        },
      });
    }

    return {
      decorations: Decoration.set(deco, true),
      atomics: RangeSet.of(atomic, true),
    };
  }
}

export const livePreview = [
  tableStyles,
  tableField,
  mermaidStyles,
  mermaidField,
  ViewPlugin.fromClass(LivePreviewPlugin, {
    decorations: (v) => v.decorations,
    provide: (plugin) =>
      EditorView.atomicRanges.of((view) => view.plugin(plugin)?.atomics ?? RangeSet.empty),
  }),
  // Click handling for rendered links / wikilinks / tags
  EditorView.domEventHandlers({
    mousedown(event, view) {
      const target = event.target as HTMLElement;
      const wikilink = target.closest<HTMLElement>("[data-wikilink]");
      if (wikilink && event.button === 0) {
        event.preventDefault();
        view.state.facet(editorCallbacks).onWikilinkClick(wikilink.dataset["wikilink"]!);
        return true;
      }
      const link = target.closest<HTMLElement>(".hn-link[data-url]");
      if (link && event.button === 0) {
        event.preventDefault();
        view.state.facet(editorCallbacks).onLinkClick(link.dataset["url"]!);
        return true;
      }
      const tag = target.closest<HTMLElement>("[data-tag]");
      if (tag && event.button === 0 && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        view.state.facet(editorCallbacks).onHashtagClick(tag.dataset["tag"]!);
        return true;
      }
      return false;
    },
  }),
  // Link-ish cursor affordances
  EditorView.theme({
    ".hn-link, .hn-wikilink": { cursor: "pointer" },
    ".hn-link:hover, .hn-wikilink:hover": { textDecoration: "underline" },
    ".hn-inline-code": {
      backgroundColor: "var(--md-code-bg)",
      borderRadius: "3px",
      padding: "1px 4px",
      fontFamily: "var(--font-mono)",
      fontSize: "0.88em",
    },
    ".hn-tag-pill": {
      backgroundColor: "var(--accent-subtle)",
      borderRadius: "9px",
      padding: "1px 7px",
      fontSize: "0.85em",
      cursor: "pointer",
    },
  }),
];
